import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";
import { Box, Button, Container, Divider, IconButton, Paper, Stack, TextField } from "@mui/material";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loading } from "src/components/utils/Loading";
import { useSnackbar } from "src/utils/useSnackbar";
import { FoodsIcon, FoodsTooltip, Typography } from "@foodpilot/foods";
import { URLHandler } from "src/services/URLHandler";
import { useCreateRole } from "src/api/client-api/roles/useCreateRole";
import { useGetAllPermissions } from "src/api/client-api/permissions/useGetAllPermissions";
import { Role } from "src/api/client-api/roles/useGetRole.ts";
import { applyPermissionDependencies, togglePermissionSelection } from "./permissionDependencies";
import {
    deriveRoleScope,
    permissionIdsOfObjectInScope,
    resolvePermissionScope,
    type PermissionScopeValue,
} from "./permissionScope";
import { PermissionScopeSelector } from "./components/PermissionScopeSelector";
import { PermissionsToolbar } from "./components/PermissionsToolbar";
import { PermissionGrid } from "./components/PermissionGrid";
import { ScopeEmptyState } from "./components/ScopeEmptyState";

type RoleCreateEditProps = {
    existingRole?: Role;
};

const RoleCreateEdit: FC<RoleCreateEditProps> = ({ existingRole }) => {
    const { t } = useTranslation();

    const roleSchema = useMemo(
        () =>
            z.object({
                displayName: z
                    .string()
                    .min(1, t("settings.roles.validation.display_name_required", "Role name is required")),
            }),
        [t],
    );
    type RoleFormData = z.infer<typeof roleSchema>;

    const navigate = useNavigate();
    const { setSnackbarProps } = useSnackbar();
    const isEditing = existingRole !== undefined;
    const isReadOnly = existingRole?.system === true;

    const createRoleMutation = useCreateRole();
    const { permissions: allPermissions, isLoading: permissionsLoading } = useGetAllPermissions();

    const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(
        () => new Set(existingRole?.permission?.map((permission) => permission["@id"]) ?? []),
    );
    const [browsedScope, setBrowsedScope] = useState<PermissionScopeValue | null>(null);
    const [search, setSearch] = useState("");
    const [activeOnly, setActiveOnly] = useState(false);
    const [saving, setSaving] = useState(false);

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors, isValid, isDirty },
    } = useForm<RoleFormData>({
        mode: "onChange",
        resolver: zodResolver(roleSchema),
        defaultValues: {
            displayName: existingRole?.displayName ?? "",
        },
    });

    const displayName = watch("displayName");

    const permissions = allPermissions ?? [];

    const committedScope = useMemo(
        () => deriveRoleScope(selectedPermissions, permissions),
        [selectedPermissions, permissions],
    );

    const { orgCount, platformCount } = useMemo(() => {
        let org = 0;
        let platform = 0;
        permissions.forEach((permission) => {
            if (resolvePermissionScope(permission) === "org") {
                org += 1;
            } else {
                platform += 1;
            }
        });
        return { orgCount: org, platformCount: platform };
    }, [permissions]);

    const effectiveScope = committedScope ?? browsedScope;

    const scopedPermissions = useMemo(() => {
        if (effectiveScope === null) {
            return [];
        }
        return permissions.filter((permission) => resolvePermissionScope(permission) === effectiveScope);
    }, [permissions, effectiveScope]);

    const activeCount = selectedPermissions.size;

    const handleGoBack = () => {
        navigate(URLHandler.settings.roles.list());
    };

    const handleBrowseScope = (scope: PermissionScopeValue) => {
        if (committedScope !== null && committedScope !== scope) {
            return;
        }
        setBrowsedScope(scope);
    };

    const handleToggle = useCallback(
        (permissionId: string) => {
            setSelectedPermissions((previous) => togglePermissionSelection(previous, permissionId, permissions));
        },
        [permissions],
    );

    const handleSelectAll = useCallback(
        (object: string) => {
            setSelectedPermissions((previous) => {
                const next = new Set([
                    ...previous,
                    ...permissionIdsOfObjectInScope(permissions, object, effectiveScope),
                ]);
                return applyPermissionDependencies(next, permissions);
            });
        },
        [permissions, effectiveScope],
    );

    const handleSelectNone = useCallback(
        (object: string) => {
            setSelectedPermissions((previous) => {
                const objectIds = permissionIdsOfObjectInScope(permissions, object, effectiveScope);
                const next = new Set([...previous].filter((id) => !objectIds.has(id)));
                return applyPermissionDependencies(next, permissions);
            });
        },
        [permissions, effectiveScope],
    );

    const onSubmit = async (data: RoleFormData) => {
        try {
            setSaving(true);
            await createRoleMutation.mutateAsync({
                isNew: !isEditing,
                roleCode: existingRole?.code,
                role: {
                    displayName: data.displayName.trim(),
                    permission: Array.from(selectedPermissions),
                },
            });
            setSnackbarProps({ message: t("settings.success"), type: "success" });
            navigate(URLHandler.settings.roles.list());
        } catch {
            setSnackbarProps({ message: t("settings.roles.save_error", "An error occurred"), type: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (permissionsLoading) {
        return <Loading />;
    }

    const hasPermissionsSelected = selectedPermissions.size > 0;
    const canSave = !isReadOnly && isValid && (isDirty || hasPermissionsSelected) && displayName.trim().length > 0;
    const isCreateButtonDisabled = !canSave || saving;
    const showEmptyState = !isEditing && committedScope === null && browsedScope === null;

    return (
        <Container maxWidth={false} sx={{ py: 3, px: 6 }}>
            <Paper
                elevation={0}
                sx={(theme) => ({
                    borderRadius: theme.spacing(1.5),
                    border: `1px solid ${theme.custom.grey[600]}`,
                    overflow: "hidden",
                })}
            >
                <Box
                    sx={(theme) => ({
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: theme.spacing(3.5, 4),
                        backgroundColor: theme.custom.grey[100],
                        borderBottom: `1px solid ${theme.custom.grey[600]}`,
                    })}
                >
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <IconButton
                            onClick={handleGoBack}
                            aria-label={t("settings.roles.back", "Back")}
                            sx={{ p: 0, "&:hover": { backgroundColor: "transparent" } }}
                        >
                            <FoodsIcon icon="arrowLeft" size={2.5} />
                        </IconButton>
                        <Typography variant="number-inter" sx={(theme) => ({ color: theme.custom.grey[2800] })}>
                            {existingRole?.displayName ?? t("settings.roles.addrole", "Add a role")}
                        </Typography>
                        {isReadOnly && (
                            <FoodsTooltip
                                title={t("settings.roles.system_role_tooltip", "System role: read-only")}
                                placement="top"
                            >
                                <Box
                                    sx={(theme) => ({
                                        display: "flex",
                                        alignItems: "center",
                                        color: theme.custom.grey[2500],
                                    })}
                                >
                                    <FoodsIcon icon="lock" size={1.25} />
                                </Box>
                            </FoodsTooltip>
                        )}
                    </Stack>
                    {!isReadOnly && (
                        <Button
                            type="submit"
                            form="role-form"
                            variant="contained"
                            disabled={isCreateButtonDisabled}
                            startIcon={<FoodsIcon icon="save" size={1.25} />}
                            sx={(theme) => ({
                                borderRadius: theme.spacing(5.5),
                                padding: theme.spacing(0, 2.5, 0, 2),
                                height: theme.spacing(5),
                                ...theme.typography["body-bold"],
                                textTransform: "none",
                                background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                color: `${theme.custom.grey[100]} !important`,
                                opacity: canSave ? 1 : 0.36,
                                "&:hover": {
                                    background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                },
                                "&:disabled": {
                                    background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                    color: `${theme.custom.grey[100]} !important`,
                                    opacity: 0.36,
                                },
                            })}
                        >
                            {t("settings.save", "Save")}
                        </Button>
                    )}
                </Box>

                <Box
                    component="form"
                    id="role-form"
                    onSubmit={handleSubmit(onSubmit)}
                    sx={(theme) => ({ padding: theme.spacing(4) })}
                >
                    <Stack spacing={4}>
                        <Stack spacing={3} sx={(theme) => ({ maxWidth: theme.spacing(66) })}>
                            <Box>
                                <Typography variant="h6" sx={(theme) => ({ color: theme.custom.grey[2800], mb: 1 })}>
                                    {t("settings.roles.name_label", "Role name")}
                                </Typography>
                                <Controller
                                    name="displayName"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            disabled={isReadOnly}
                                            placeholder={t("settings.roles.name_placeholder", "Role name")}
                                            variant="outlined"
                                            fullWidth
                                            error={!!errors.displayName}
                                            helperText={errors.displayName?.message}
                                            sx={(theme) => ({
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: theme.spacing(3),
                                                    height: theme.spacing(5),
                                                    "& fieldset": { borderColor: theme.custom.grey[600] },
                                                    "&:hover fieldset": { borderColor: theme.custom.grey[600] },
                                                    "&.Mui-focused fieldset": {
                                                        borderColor: theme.palette.primary[600],
                                                    },
                                                },
                                                "& .MuiInputBase-input": {
                                                    padding: theme.spacing(0, 1.5, 0, 2.5),
                                                    ...theme.typography["body-medium"],
                                                },
                                            })}
                                        />
                                    )}
                                />
                            </Box>
                        </Stack>

                        <Divider sx={(theme) => ({ borderColor: theme.custom.grey[600] })} />

                        <PermissionScopeSelector
                            browsedScope={browsedScope}
                            committedScope={committedScope}
                            orgCount={orgCount}
                            platformCount={platformCount}
                            onBrowseScope={handleBrowseScope}
                        />

                        {showEmptyState ?
                            <ScopeEmptyState onBrowseScope={handleBrowseScope} />
                        :   <Stack spacing={2}>
                                <PermissionsToolbar
                                    search={search}
                                    onSearchChange={setSearch}
                                    activeOnly={activeOnly}
                                    onActiveOnlyChange={setActiveOnly}
                                    activeCount={activeCount}
                                />
                                <Box sx={isReadOnly ? { pointerEvents: "none", opacity: 0.6 } : undefined}>
                                    <PermissionGrid
                                        scopedPermissions={scopedPermissions}
                                        selectedIds={selectedPermissions}
                                        search={search}
                                        activeOnly={activeOnly}
                                        onToggle={handleToggle}
                                        onSelectAll={handleSelectAll}
                                        onSelectNone={handleSelectNone}
                                    />
                                </Box>
                            </Stack>
                        }
                    </Stack>
                </Box>
            </Paper>
        </Container>
    );
};

export default RoleCreateEdit;

export const RoleCreate = RoleCreateEdit;
